import { useRef, useState } from "react";
import { Pencil } from "lucide-react";

import { LEAVE_BALANCE, LEAVE_USAGE_SUMMARY } from "../../../../../../config/Endpoints.js";
import {
  Button,
  Modal,
  ResponsiveDataTable,
  Select,
  TextField,
  type ResponsiveColumn,
} from "../../../../ui/index.ts";
import { EmptyStateMessage } from "../../../../shared/empty-state-message/empty-state-message.tsx";
import { getApi, putApi } from "../../../../../utils/api.ts";
import { accessToken } from "../../../../../utils/auth.ts";
import { showError, showSuccess } from "../../../../../utils/toast.ts";
import type { EmployeeOption, LeaveBalanceRow, LeaveUsageSummary } from "../../types.ts";
import { EmployeeTypeEnum } from "../../../../../../constants/employeeType.js";
import { extractMessage } from "../../hooks.ts";
import styles from "./leave-balance-tab.module.css";

interface LeaveBalanceTabProps {
  employeeOptions: EmployeeOption[];
  employeesLoading: boolean;
}

type LeaveBalanceForm = {
  privilegeLeave: string;
  casualLeave: string;
  workFromHomeLeave: string;
  quartelyLeave: string;
  shortLeave: string;
};

const EMPTY_FORM: LeaveBalanceForm = {
  privilegeLeave: "0",
  casualLeave: "0",
  workFromHomeLeave: "0",
  quartelyLeave: "0",
  shortLeave: "0",
};

function normalizeLeaveValue(value: unknown): string {
  if (value == null || value === "") return "0";
  return String(value);
}

function balanceToForm(balance: LeaveBalanceRow | null): LeaveBalanceForm {
  return {
    privilegeLeave: normalizeLeaveValue(balance?.privilegeLeave),
    casualLeave: normalizeLeaveValue(balance?.casualLeave),
    workFromHomeLeave: normalizeLeaveValue(balance?.workFromHomeLeave),
    quartelyLeave: normalizeLeaveValue(balance?.quartelyLeave),
    shortLeave: normalizeLeaveValue(balance?.shortLeave),
  };
}

const REGULAR_LEAVE_FIELDS = [
  { key: "privilegeLeave", label: "Privilege Leave" },
  { key: "casualLeave", label: "Casual Leave" },
  { key: "workFromHomeLeave", label: "Work From Home" },
] as const;

const SQE_LEAVE_FIELDS = [
  { key: "privilegeLeave", label: "Privilege Leave" },
  { key: "quartelyLeave", label: "Quarterly Leave" },
  { key: "shortLeave", label: "Short Leave" },
] as const;

const MAX_ONE_LEAVE_FIELDS: ReadonlyArray<keyof LeaveBalanceForm> = [
  "casualLeave",
  "quartelyLeave",
  "shortLeave",
];

function isValidPrivilegeLeave(value: string): boolean {
  return /^\d+(?:\.5)?$/.test(value) && Number(value) <= 25;
}


export function LeaveBalanceTab({
  employeeOptions,
  employeesLoading,
}: LeaveBalanceTabProps) {
  const [employeeId, setEmployeeId] = useState("");
  const [balance, setBalance] = useState<LeaveBalanceRow | null>(null);
  const [formData, setFormData] = useState<LeaveBalanceForm>(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [usageSummary, setUsageSummary] = useState<LeaveUsageSummary | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState("");
  const selectedEmployeeIdRef = useRef("");
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const selectedEmployee = employeeOptions.find(
    (item) => item.value === employeeId,
  );
  const isSqeEmployee =
    String(selectedEmployee?.empType ?? "")
      .replace(/[^a-zA-Z]/g, "")
      .toUpperCase() === EmployeeTypeEnum.SQELABS;


  const activeLeaveFields = isSqeEmployee
    ? SQE_LEAVE_FIELDS
    : REGULAR_LEAVE_FIELDS;

  const fetchBalance = async (
    nextEmployeeId: string,
    options: { preserveCurrent?: boolean } = {},
  ) => {
    if (!nextEmployeeId) {
      setBalance(null);
      setFormData(EMPTY_FORM);
      return;
    }

    if (!options.preserveCurrent) {
      setLoading(true);
      setBalance(null);
    }
    try {
      const { data } = await getApi<{ data?: LeaveBalanceRow }>(
        `${LEAVE_BALANCE}/${nextEmployeeId}`,
        accessToken(),
      );
      if (selectedEmployeeIdRef.current !== nextEmployeeId) return;
      const row = data?.data ?? null;
      setBalance(row ? { ...row, id: nextEmployeeId } : null);
      setFormData(balanceToForm(row));
    } catch (e) {
      if (selectedEmployeeIdRef.current !== nextEmployeeId) return;
      showError(extractMessage(e, "Failed to load leave balance."));
      if (!options.preserveCurrent) {
        setBalance(null);
        setFormData(EMPTY_FORM);
      }
    } finally {
      if (!options.preserveCurrent && selectedEmployeeIdRef.current === nextEmployeeId) setLoading(false);
    }
  };

  const fetchUsageSummary = async (nextEmployeeId: string) => {
    if (!nextEmployeeId) {
      setUsageSummary(null);
      setUsageError("");
      setUsageLoading(false);
      return;
    }

    setUsageLoading(true);
    setUsageSummary(null);
    setUsageError("");
    try {
      const { data } = await getApi<{ data?: LeaveUsageSummary }>(
        `${LEAVE_USAGE_SUMMARY}/${nextEmployeeId}/usage-summary`,
        accessToken(),
      );
      if (selectedEmployeeIdRef.current === nextEmployeeId) {
        setUsageSummary(data?.data ?? null);
      }
    } catch (e) {
      if (selectedEmployeeIdRef.current === nextEmployeeId) {
        setUsageError(extractMessage(e, "Failed to load leave requests."));
      }
    } finally {
      if (selectedEmployeeIdRef.current === nextEmployeeId) setUsageLoading(false);
    }
  };

  const handleEmployeeChange = (nextEmployeeId: string) => {
    selectedEmployeeIdRef.current = nextEmployeeId;
    setEmployeeId(nextEmployeeId);
    void fetchBalance(nextEmployeeId);
    void fetchUsageSummary(nextEmployeeId);
  };

  const updateFormValue = (key: keyof LeaveBalanceForm, value: string) => {
    if (value === "") {
      setFormData((prev) => ({
        ...prev,
        [key]: "",
      }));
      return;
    }

    if (key === "privilegeLeave") {
      const normalized = value.replace(/[^\d.]/g, "");

      // Privilege leave supports whole days and half-day increments.
      if (!/^\d{0,2}(?:\.5?)?$/.test(normalized)) return;

      const asNumber = Number(normalized);
      if (normalized !== "." && asNumber > 25) return;

      setFormData((prev) => ({
        ...prev,
        [key]: normalized,
      }));
      return;
    }

    const numeric = value.replace(/\D/g, "").slice(0, 2);
    const asNumber = Number(numeric);

    const maxValue = MAX_ONE_LEAVE_FIELDS.includes(key) ? 99 : 25;

    setFormData((prev) => ({
      ...prev,
      [key]: String(Math.min(maxValue, asNumber)),
    }));
  };

  const resetForm = () => {
    setFormData(balanceToForm(balance));
  };

  const handleSubmit = async () => {
    if (!employeeId) {
      showError("Please select employee.");
      return;
    }

    if (!isValidPrivilegeLeave(formData.privilegeLeave)) {
      return;
    }

    const hasMaxOneError = activeLeaveFields.some(
      ({ key }) =>
        MAX_ONE_LEAVE_FIELDS.includes(key) && Number(formData[key] || 0) > 1,
    );
    if (hasMaxOneError) {
      return;
    }

    setSubmitting(true);
    try {
      const payload: Record<string, number | string> = {
        employeeId,
      };

      activeLeaveFields.forEach(({ key }) => {
        payload[key] = Number(formData[key] || 0);
      });

      const { data } = await putApi<{ data?: LeaveBalanceRow; message?: string }>(
        LEAVE_BALANCE,
        accessToken(),
        payload,
      );

      const submittedCounts = Object.fromEntries(
        activeLeaveFields.map(({ key }) => [key, Number(formData[key] || 0)]),
      );
      setBalance((current) => ({
        ...(current ?? { id: employeeId }),
        ...submittedCounts,
        id: employeeId,
      }));
      setFormData((current) => ({ ...current }));
      setModalOpen(false);

      showSuccess(data?.message || "Leave balance updated successfully.");
      await fetchBalance(employeeId, { preserveCurrent: true });
    } catch (e) {
      showError(extractMessage(e, "Failed to update leave balance."));
    } finally {
      setSubmitting(false);
    }
  };

  const rows = balance ? [balance] : [];

  const openUpdateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const columns: ResponsiveColumn<LeaveBalanceRow>[] = [
      ...activeLeaveFields.map(({ key, label }) => ({
        id: key,
        header: label,
        accessor: key,
        sortable: true,
        nowrap: true,
        render: (row: LeaveBalanceRow) => {
          const value = row[key as keyof LeaveBalanceRow];

          return value == null ? "-" : String(value);
        },

      })),
      {
        id: "actions",
        header: "Action",
        sortable: false,
        nowrap: true,
        render: () => (
          <Button
            size="sm"
            startIcon={<Pencil size={14} aria-hidden />}
            onClick={openUpdateModal}
          >
            Update
          </Button>
        ),
      },
    ];

  return (
    <div className={styles.stack}>
      <section className={styles.panel}>

        <div className={styles.employeeSelect}>
          <Select
            isClearable
            id="leave-balance-employee"
            label="Employee Name"
            value={employeeId}
            onChange={handleEmployeeChange}
            options={employeeOptions}
            placeholder={employeesLoading ? "Loading..." : "Select Employee Name"}
            searchable
            disabled={employeesLoading || employeeOptions.length === 0}
          />
        </div>
      </section>

      <section >
        <ResponsiveDataTable
          columns={columns}
          data={rows}
          aria-label="Leave balance"
          pagination={false}
          emptyState={
            <EmptyStateMessage
              message={
                loading
                  ? "Loading..."
                  : "Select an employee to view leave balance."
              }
            />
          }
        />
      </section>

      {employeeId && (
        <section className={styles.panel} aria-label="Leave request summary">
          <h2 className={styles.summaryHeading}>Applied Leaves</h2>
          {usageLoading ? (
            <p role="status">Loading leave requests...</p>
          ) : usageError ? (
            <p role="alert">{usageError}</p>
          ) : usageSummary?.leaveTypes.length ? (
            <div className={styles.summaryScroll}>
              <table className={styles.summaryTable}>
                <thead>
                  <tr>
                    <th scope="col">Leave Type</th>
                    <th scope="col">Pending Requests</th>
                    <th scope="col">Pending Days</th>
                    <th scope="col">Approved Requests</th>
                    <th scope="col">Approved Days</th>
                  </tr>
                </thead>
                <tbody>
                  {usageSummary.leaveTypes.map((row) => (
                    <tr key={row.leaveType}>
                      <th scope="row">{row.leaveType}</th>
                      <td>{row.pendingRequests}</td>
                      <td>{row.pendingDays}</td>
                      <td>{row.approvedRequests}</td>
                      <td>{row.approvedDays}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No pending or approved leave requests from this month onward.</p>
          )}
        </section>
      )}

      <Modal
        open={modalOpen}
        onClose={() => {
          resetForm();
          setModalOpen(false);
        }}
        title="Leave Update"
        size="md"
        footer={
          <>
            <div className={styles.modalFooter}>
              <Button
                variant="light-grey"
                onClick={() => {
                  resetForm();
                  setModalOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={submitting}>
                {submitting ? "Saving..." : "Submit"}
              </Button>
            </div>
          </>
        }
      >
        <div className={styles.modalGrid}>
          {activeLeaveFields.map(({ key, label }) => (
            <TextField
              key={key}
              id={key}
              label={label}
              value={formData[key]}
              inputMode={key === "privilegeLeave" ? "decimal" : "numeric"}
              required
              error={
                key === "privilegeLeave" &&
                !isValidPrivilegeLeave(formData[key])
                  ? "Enter a whole number or a half-day value, up to 25."
                  : MAX_ONE_LEAVE_FIELDS.includes(key) &&
                Number(formData[key] || 0) > 1
                  ? "Maximum value allowed is 1."
                  : undefined
              }
              onChange={(event) =>
                updateFormValue(key, event.target.value)
              }
            />
          ))}
        </div>
      </Modal>
    </div>
  );
}

